import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { api, TOKEN_KEY, ADMIN_KEY } from 'lib/api';
import { API_ENDPOINTS } from 'constants/endpoints';

export interface AdminInfo {
  id: number;
  username: string;
  email: string;
  roleId: number | null;
  roleName: string | null;
  permissions: string[];
  isSuperAdmin: boolean;
}

interface LoginResponseData {
  token: string;
  admin: AdminInfo;
}

interface AuthApi {
  admin: AdminInfo | null;
  token: string | null;
  login: (email: string, password: string) => Promise<{ ok: boolean; message?: string }>;
  logout: () => void;
  /** True if the current admin can access the given module key (Super Admin always can). */
  hasPermission: (moduleKey: string) => boolean;
}

const AuthContext = createContext<AuthApi | null>(null);

const readStoredAdmin = (): AdminInfo | null => {
  try {
    const raw = localStorage.getItem(ADMIN_KEY);
    return raw ? (JSON.parse(raw) as AdminInfo) : null;
  } catch {
    return null;
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [admin, setAdmin] = useState<AdminInfo | null>(() => readStoredAdmin());

  // Refresh permissions from the server on load so newly-added module keys
  // (e.g. consumers) appear without forcing a re-login.
  React.useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    if (!storedToken) return;

    api.get<AdminInfo>(API_ENDPOINTS.ADMIN.AUTH.ME)
      .then((res) => {
        if (res.success && res.data) {
          localStorage.setItem(ADMIN_KEY, JSON.stringify(res.data));
          setAdmin(res.data);
        }
      })
      .catch(() => undefined);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    try {
      const res = await api.post<LoginResponseData>(API_ENDPOINTS.ADMIN.AUTH.LOGIN, { email, password });
      const payload = res.data as LoginResponseData | undefined;

      if (res.success && payload?.token && payload?.admin) {
        localStorage.setItem(TOKEN_KEY, payload.token);
        localStorage.setItem(ADMIN_KEY, JSON.stringify(payload.admin));
        setToken(payload.token);
        setAdmin(payload.admin);
        return { ok: true };
      }

      return { ok: false, message: res.message === 'Unauthorized' ? 'Invalid username or password.' : (res.message || 'Login failed.') };
    } catch {
      return { ok: false, message: 'Unable to reach the server. Please try again.' };
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ADMIN_KEY);
    setToken(null);
    setAdmin(null);
  }, []);

  const hasPermission = useCallback(
    (moduleKey: string) => {
      if (!admin) return false;
      if (admin.isSuperAdmin) return true;
      return Array.isArray(admin.permissions) && admin.permissions.includes(moduleKey);
    },
    [admin]
  );

  const value = useMemo<AuthApi>(() => ({ admin, token, login, logout, hasPermission }), [admin, token, login, logout, hasPermission]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthApi => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};
